import { NavLink, Route, Routes } from 'react-router-dom';
import { Server, Wallet, MessageSquare, User } from 'lucide-react';
import { useMe } from './api/hooks';
import { Spinner } from './components/ui';
import { useT } from './i18n';
import { getInitData, getTg } from './lib/tg';
import { OutsideTelegram } from './pages/OutsideTelegram';
import { InstanceList } from './pages/InstanceList';
import { InstanceDetail } from './pages/InstanceDetail';
import { Login } from './pages/Login';
import { Totp } from './pages/Totp';
import { Billing } from './pages/Billing';
import { InvoiceDetail } from './pages/InvoiceDetail';
import { Tickets } from './pages/Tickets';
import { TicketDetail } from './pages/TicketDetail';
import { TicketNew } from './pages/TicketNew';
import { Profile } from './pages/Profile';
import { Alerts } from './pages/Alerts';

export function App() {
  // Hard guard: outside Telegram, initData is empty → no auth can work.
  if (!getTg() || getInitData().length === 0) {
    return <OutsideTelegram />;
  }

  const me = useMe();

  if (me.isLoading) {
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Spinner size={28} />
      </div>
    );
  }

  if (!me.data?.loggedIn) {
    return (
      <Routes>
        <Route path="/totp" element={<Totp />} />
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }

  return (
    <div
      style={{
        paddingBottom: `calc(var(--tab-h) + var(--safe-bottom) + 8px)`,
        paddingTop: 'var(--safe-top)',
        minHeight: '100vh',
      }}
    >
      <Routes>
        <Route path="/" element={<InstanceList />} />
        <Route path="/instances/:uuid" element={<InstanceDetail />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/billing/invoices/:uuid" element={<InvoiceDetail />} />
        <Route path="/tickets" element={<Tickets />} />
        <Route path="/tickets/new" element={<TicketNew />} />
        <Route path="/tickets/:id" element={<TicketDetail />} />
        <Route path="/me" element={<Profile />} />
        <Route path="/me/alerts" element={<Alerts />} />
        <Route path="*" element={<InstanceList />} />
      </Routes>
      <TabBar />
    </div>
  );
}

function TabBar() {
  const t = useT();

  return (
    <nav
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        display: 'flex',
        background: 'var(--tg-section-bg)',
        borderTop: '0.5px solid var(--tg-separator)',
        paddingBottom: 'var(--safe-bottom)',
        zIndex: 10,
        backdropFilter: 'saturate(180%) blur(20px)',
        WebkitBackdropFilter: 'saturate(180%) blur(20px)',
      }}
    >
      <Tab to="/" label={t('tab.vps')} icon={Server} />
      <Tab to="/billing" label={t('tab.billing')} icon={Wallet} />
      <Tab to="/tickets" label={t('tab.tickets')} icon={MessageSquare} />
      <Tab to="/me" label={t('tab.me')} icon={User} />
    </nav>
  );
}

function Tab({
  to,
  label,
  icon: Icon,
  badge,
}: {
  to: string;
  label: string;
  icon: typeof Server;
  badge?: number;
}) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      style={({ isActive }) => ({
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        padding: '8px 0 6px',
        color: isActive ? 'var(--tg-button)' : 'var(--tg-hint)',
        fontWeight: isActive ? 600 : 500,
        transition: 'color 0.15s',
        position: 'relative',
      })}
    >
      {({ isActive }) => (
        <>
          <span style={{ position: 'relative' }}>
            <Icon size={22} strokeWidth={isActive ? 2.4 : 2} />
            {badge && badge > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: -4,
                  right: -8,
                  minWidth: 16,
                  height: 16,
                  padding: '0 4px',
                  background: 'var(--danger)',
                  color: '#fff',
                  borderRadius: 8,
                  fontSize: 10,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1,
                }}
              >
                {badge > 99 ? '99+' : badge}
              </span>
            )}
          </span>
          <span style={{ fontSize: 11, letterSpacing: '0.01em' }}>{label}</span>
        </>
      )}
    </NavLink>
  );
}

